/**
 * PDP lightbox zoom — pinch/pan, wheel zoom, side arrows + toolbar.
 * Chrome lives in product-media-modal.liquid (not JS-injected) so real
 * mobile always has visible prev/next affordances.
 */
(function () {
  'use strict';

  var MIN_SCALE = 1;
  var MAX_SCALE = 4;
  var STEP = 0.35;
  var SWIPE_THRESHOLD = 48;

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function initModal(modal) {
    if (!modal || modal.dataset.pdpZoom === '1') return;
    modal.dataset.pdpZoom = '1';

    var dialog = modal.querySelector('.product-media-modal__dialog');
    var content = modal.querySelector('.product-media-modal__content');
    var ui = modal.querySelector('[data-pdp-zoom-ui]');
    if (!dialog || !content || !ui) return;

    content.classList.add('pdp-zoom__source');

    var state = {
      scale: 1,
      x: 0,
      y: 0,
      index: 0,
      pointers: new Map(),
      lastPinch: 0,
      panning: false,
      swipeStartX: 0,
      swipeStartY: 0,
      swiping: false,
    };

    var stage = modal.querySelector('[data-pdp-zoom-stage]');
    var imgWrap = modal.querySelector('[data-pdp-zoom-target]');
    var counter = modal.querySelector('[data-pdp-zoom-counter]');
    var zoomInBtn = modal.querySelector('[data-pdp-zoom-in]');
    var zoomOutBtn = modal.querySelector('[data-pdp-zoom-out]');
    var zoomResetBtn = modal.querySelector('[data-pdp-zoom-reset]');
    var prevBtns = modal.querySelectorAll('[data-pdp-zoom-prev]');
    var nextBtns = modal.querySelectorAll('[data-pdp-zoom-next]');

    function mediaList() {
      var seen = {};
      var list = [];
      Array.prototype.forEach.call(content.querySelectorAll('[data-media-id]'), function (node) {
        if (!node.matches('img[data-media-id], deferred-media[data-media-id], .product-media-modal__model[data-media-id]')) {
          return;
        }
        var id = String(node.getAttribute('data-media-id') || '');
        if (!id || seen[id]) return;
        seen[id] = true;
        list.push(node);
      });
      return list;
    }

    function applyTransform() {
      if (!imgWrap) return;
      imgWrap.style.transform =
        'translate3d(' + state.x + 'px,' + state.y + 'px,0) scale(' + state.scale + ')';
      if (stage) stage.classList.toggle('is-zoomed', state.scale > 1.01);
      if (zoomResetBtn) zoomResetBtn.disabled = state.scale <= 1.01;
      if (zoomOutBtn) zoomOutBtn.disabled = state.scale <= MIN_SCALE + 0.01;
      if (zoomInBtn) zoomInBtn.disabled = state.scale >= MAX_SCALE - 0.01;
    }

    function resetZoom() {
      state.scale = 1;
      state.x = 0;
      state.y = 0;
      applyTransform();
    }

    function setScale(next, originX, originY) {
      var prev = state.scale;
      state.scale = clamp(next, MIN_SCALE, MAX_SCALE);
      if (state.scale === 1) {
        state.x = 0;
        state.y = 0;
      } else if (typeof originX === 'number' && typeof originY === 'number' && prev > 0 && stage) {
        var rect = stage.getBoundingClientRect();
        var cx = originX - rect.left - rect.width / 2;
        var cy = originY - rect.top - rect.height / 2;
        var ratio = state.scale / prev;
        state.x = cx - (cx - state.x) * ratio;
        state.y = cy - (cy - state.y) * ratio;
      }
      applyTransform();
    }

    function updateCounter() {
      var list = mediaList();
      var multi = list.length > 1;
      if (counter) {
        counter.textContent = list.length ? state.index + 1 + ' / ' + list.length : '';
      }
      prevBtns.forEach(function (btn) {
        btn.disabled = !multi;
        btn.setAttribute('aria-disabled', multi ? 'false' : 'true');
        btn.hidden = !multi;
      });
      nextBtns.forEach(function (btn) {
        btn.disabled = !multi;
        btn.setAttribute('aria-disabled', multi ? 'false' : 'true');
        btn.hidden = !multi;
      });
    }

    function mountIndex(index) {
      var list = mediaList();
      if (!list.length || !imgWrap) return;

      state.index = ((index % list.length) + list.length) % list.length;
      var active = list[state.index];
      var id = String(active.getAttribute('data-media-id'));

      list.forEach(function (n) {
        n.classList.toggle('active', String(n.getAttribute('data-media-id')) === id);
      });

      imgWrap.innerHTML = '';
      var clone;
      if (active.tagName === 'IMG') {
        clone = active.cloneNode(true);
        clone.removeAttribute('sizes');
        clone.loading = 'eager';
        clone.draggable = false;
      } else {
        clone = document.createElement('img');
        var srcImg = active.querySelector('img');
        clone.src = srcImg ? srcImg.currentSrc || srcImg.src : '';
        clone.alt = srcImg ? srcImg.alt || '' : '';
        clone.draggable = false;
      }
      clone.classList.add('pdp-zoom__image');
      clone.removeAttribute('data-media-id');
      imgWrap.appendChild(clone);
      resetZoom();
      updateCounter();
    }

    function go(delta) {
      mountIndex(state.index + delta);
    }

    function indexFromOpener(opener) {
      var list = mediaList();
      if (!opener || !list.length) return 0;
      var id = String(opener.getAttribute('data-media-id') || '');
      var idx = list.findIndex(function (n) {
        return String(n.getAttribute('data-media-id')) === id;
      });
      return idx < 0 ? 0 : idx;
    }

    function openAt(opener) {
      ui.hidden = false;
      modal.classList.add('pdp-zoom-ready');
      mountIndex(indexFromOpener(opener || modal.openedBy));
    }

    function closeUi() {
      resetZoom();
      modal.classList.remove('pdp-zoom-ready');
      ui.hidden = true;
    }

    if (stage) {
      stage.addEventListener(
        'wheel',
        function (event) {
          if (!modal.hasAttribute('open')) return;
          event.preventDefault();
          setScale(state.scale + (event.deltaY < 0 ? STEP : -STEP), event.clientX, event.clientY);
        },
        { passive: false }
      );

      stage.addEventListener('dblclick', function (event) {
        event.preventDefault();
        if (state.scale > 1.01) resetZoom();
        else setScale(2, event.clientX, event.clientY);
      });

      stage.addEventListener('pointerdown', function (event) {
        if (event.button !== undefined && event.button !== 0) return;
        try {
          stage.setPointerCapture(event.pointerId);
        } catch (err) {}
        state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (state.pointers.size === 2) {
          state.swiping = false;
          var pts = Array.from(state.pointers.values());
          state.lastPinch = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        } else if (state.scale > 1.01) {
          state.panning = true;
          state.swiping = false;
        } else {
          state.panning = false;
          state.swiping = true;
          state.swipeStartX = event.clientX;
          state.swipeStartY = event.clientY;
        }
      });

      stage.addEventListener('pointermove', function (event) {
        if (!state.pointers.has(event.pointerId)) return;
        var prevPt = state.pointers.get(event.pointerId);
        state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (state.pointers.size === 2) {
          var pts = Array.from(state.pointers.values());
          var dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
          if (state.lastPinch) {
            setScale(
              state.scale * (dist / state.lastPinch),
              (pts[0].x + pts[1].x) / 2,
              (pts[0].y + pts[1].y) / 2
            );
          }
          state.lastPinch = dist;
          return;
        }

        if (state.panning && state.scale > 1.01) {
          state.x += event.clientX - prevPt.x;
          state.y += event.clientY - prevPt.y;
          applyTransform();
        }
      });

      function endPointer(event) {
        if (state.swiping && state.pointers.size === 1 && state.scale <= 1.01) {
          var dx = event.clientX - state.swipeStartX;
          var dy = event.clientY - state.swipeStartY;
          if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
            go(dx < 0 ? 1 : -1);
          }
        }
        state.pointers.delete(event.pointerId);
        if (state.pointers.size < 2) state.lastPinch = 0;
        if (state.pointers.size === 0) {
          state.panning = false;
          state.swiping = false;
        }
      }

      stage.addEventListener('pointerup', endPointer);
      stage.addEventListener('pointercancel', endPointer);
    }

    function bindNav(btn, delta) {
      if (!btn) return;
      var handler = function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        go(delta);
      };
      btn.addEventListener('click', handler);
      btn.addEventListener('pointerup', function (e) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      });
    }

    prevBtns.forEach(function (btn) {
      bindNav(btn, -1);
    });
    nextBtns.forEach(function (btn) {
      bindNav(btn, 1);
    });

    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        setScale(state.scale + STEP);
      });
    }
    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        setScale(state.scale - STEP);
      });
    }
    if (zoomResetBtn) {
      zoomResetBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        resetZoom();
      });
    }

    function wrapMethod(name, afterFn, beforeFn) {
      var proto = Object.getPrototypeOf(modal);
      var fromProto = proto && typeof proto[name] === 'function';
      var original = fromProto ? proto[name] : modal[name];
      if (typeof original !== 'function') return;

      modal[name] = function () {
        if (beforeFn) beforeFn.apply(modal, arguments);
        var result = original.apply(modal, arguments);
        if (afterFn) afterFn.apply(modal, arguments);
        return result;
      };
    }

    wrapMethod('show', function (opener) {
      requestAnimationFrame(function () {
        openAt(opener);
      });
    });

    wrapMethod('hide', null, function () {
      closeUi();
    });

    var observer = new MutationObserver(function () {
      if (modal.hasAttribute('open')) {
        openAt(modal.openedBy);
      } else {
        closeUi();
      }
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });

    modal.addEventListener('pdp:zoomopen', function (event) {
      openAt((event.detail && event.detail.opener) || modal.openedBy);
    });
  }

  function init() {
    document.querySelectorAll('product-modal.product-media-modal').forEach(initModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init if modal is moved/replaced late
  document.addEventListener('shopify:section:load', init);
})();
